import { getApiProjectsProjectIdDashboardsQueryOptions } from '@shape-construction/api/src/hooks';
import { type DashboardSchema, dashboardCategoryEnum, type ProjectSchema } from '@shape-construction/api/src/types';
import { useQuery } from '@tanstack/react-query';
import { groupBy } from 'es-toolkit';
import { useTranslation } from 'react-i18next';
import type { GroupedDashboards } from '../types';

export const useGroupedDashboards = (projectId: ProjectSchema['id']) => {
  const internalDashboards = useInternalDashboards();
  const { data: externalDashboards, isLoading, isError } = useExternalDashboards(projectId);

  const categories = Object.values(dashboardCategoryEnum);

  const mergedDashboards = Object.fromEntries(
    categories.map((category) => [
      category,
      [...internalDashboards[category], ...(externalDashboards?.[category] ?? [])],
    ])
  ) as GroupedDashboards;

  return { data: mergedDashboards, isLoading, isError };
};

const useExternalDashboards = (projectId: ProjectSchema['id']) => {
  const groupDashboardsByCategory = (dashboards: Array<DashboardSchema>) =>
    groupBy(dashboards, (dashboard) => dashboard.category) as GroupedDashboards;

  return useQuery({
    ...getApiProjectsProjectIdDashboardsQueryOptions(projectId),
    select: ({ entries }) => groupDashboardsByCategory(entries),
  });
};

const useInternalDashboards = (): GroupedDashboards => {
  const { t } = useTranslation();

  return {
    shift_manager: [
      {
        id: 'heatmap',
        title: t('dataBook.page.shiftReportsHeatmap.title'),
        description: t('dataBook.page.shiftReportsHeatmap.description'),
        category: 'shift_manager',
        pro: false,
        sortPosition: 0,
        teamId: null,
      },
    ] as const,
    issue_tracker: [
      {
        id: 'heatmap',
        title: t('dataBook.page.issuesHealthHeatmap.title'),
        description: t('dataBook.page.issuesHealthHeatmap.description'),
        category: 'issue_tracker',
        pro: false,
        sortPosition: 0,
        teamId: null,
      } as const,
    ],
    change_tracker: [],
    channels: [],
    custom: [],
    activities: [
      {
        id: 'gantt-view',
        title: t('dataBook.page.ganttView.title'),
        description: t('dataBook.page.ganttView.description'),
        category: 'activities',
        pro: false,
        sortPosition: 0,
        teamId: null,
      },
    ] as const,
  };
};
